import { ReactElement, useEffect, useState } from 'react'
import { useFlags } from '@equipmentshare/feature-flags'
import { datadogRum } from '@datadog/browser-rum'
import { DesignSystemProvider } from 'contexts/design-system'
import { MUI_VIEWS_RELEASED, MuiRolloutView, useMuiRollout } from 'config/muiRollout'

// Long enough for a normal flag fetch; past it, the view falls back to EUI rather than stay blank.
export const FLAG_WAIT_MS = 1500

export const DESIGN_SYSTEM_PROPERTY = 'design_system'

type HeapEventPropertiesApi = {
	addEventProperties?: ( properties: Record<string, string> ) => void,
	removeEventProperty?: ( name: string ) => void,
}

// Heaper doesn't wrap Heap's global event properties, so reach the snippet's `window.heap` directly.
function getHeap() {
	return ( window as Window & { heap?: HeapEventPropertiesApi } ).heap
}

export type MuiRolloutProps = {
	view: MuiRolloutView,
	eui: ReactElement,
	mui: ReactElement,
}

function MuiRolloutInner( { view, eui, mui }: MuiRolloutProps ) {
	const isMui = useMuiRollout( view )
	// LDProvider renders before flags arrive with `{}`, so rendering on the first answer would flash EUI, then remount MUI.
	const flagsReady = Object.keys( useFlags() ).length > 0
	const [ timedOut, setTimedOut ] = useState( false )
	// Decided once per mount: a targeting change streamed mid-session must not remount the view and drop its state.
	const [ decision, setDecision ] = useState<'eui' | 'mui'>()

	const waiting = MUI_VIEWS_RELEASED[ view ] && !flagsReady && !timedOut

	useEffect( () => {
		if ( !waiting ) {
			return undefined
		}
		const timer = setTimeout( () => setTimedOut( true ), FLAG_WAIT_MS )
		return () => clearTimeout( timer )
	}, [ waiting ] )

	useEffect( () => {
		if ( !decision ) {
			return undefined
		}
		// Tags every Heap event (autocaptured ones too) and the RUM view, so a report can be traced to the version seen.
		getHeap()?.addEventProperties?.( { [ DESIGN_SYSTEM_PROPERTY ]: decision } )
		if ( datadogRum.getInitConfiguration() !== undefined ) {
			datadogRum.setViewContextProperty( DESIGN_SYSTEM_PROPERTY, decision )
		}
		// RUM view context resets on navigation by itself; Heap's would leak into views that aren't converted.
		return () => getHeap()?.removeEventProperty?.( DESIGN_SYSTEM_PROPERTY )
	}, [ decision ] )

	if ( waiting ) {
		return null
	}

	const resolved = decision ?? ( isMui && flagsReady ? 'mui' : 'eui' )
	if ( decision === undefined ) {
		setDecision( resolved )
	}

	return resolved === 'mui'
		? <DesignSystemProvider value="mui">{mui}</DesignSystemProvider>
		: eui
}

// The provider lives here so no converted view can forget it.
export default function MuiRollout( props: MuiRolloutProps ) {
	// Sibling routes reuse one instance, so key by view to keep the decision, flag wait and tagging per view.
	return <MuiRolloutInner key={props.view} {...props} />
}
